'use client';

/**
 * Design System - Theme Context
 * Contexto para gestión de temas con persistencia híbrida
 */

import React, { createContext, useContext, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useTheme as useNextTheme, ThemeProvider as NextThemesProvider } from 'next-themes';
import type {
  ThemeMode,
  ResolvedTheme,
  ThemeName,
  ThemeContextValue,
  ThemeProviderProps,
} from '../themes/types';
import { createThemeBroadcastChannel } from '../utils/persistence';

// =================================
// CONSTANTS (centralizadas)
// =================================

const THEME_CONFIG = {
  /**
   * Key para localStorage. Contrato de familia (DS-5 / ADR-0028 dec. 6): 'sc-theme'
   * (reemplaza a la key legacy previa). La MISMA key la lee next-themes (y su
   * script anti-flash pre-hidratación) y el `theme-switcher` de familia.
   */
  storageKey: 'sc-theme',
  // El nombre del canal de broadcast vive en una ÚNICA constante
  // (`BROADCAST_CHANNEL_NAME` en utils/persistence.ts); acá era literal muerto.
  /** Debounce en ms para sincronización con servidor */
  syncDebounceMs: 1000,
  /** Valores de tema válidos (binario: `system` fue DROPeado en DS-5) */
  validThemes: ['light', 'dark'] as const,
} as const;

// =================================
// CONTEXT
// =================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// =================================
// INTERNAL PROVIDER (wraps next-themes)
// =================================

interface InternalThemeProviderProps {
  children: React.ReactNode;
  initialUserId?: string | number | null;
  syncDebounceMs?: number;
}

function InternalThemeProvider({
  children,
  initialUserId,
  syncDebounceMs = THEME_CONFIG.syncDebounceMs,
}: InternalThemeProviderProps) {
  const {
    theme,
    setTheme: setNextTheme,
    resolvedTheme,
  } = useNextTheme();

  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [userId, setUserIdState] = useState<string | number | null | undefined>(initialUserId);

  // Ref para el canal de broadcast
  const broadcastRef = useRef<ReturnType<typeof createThemeBroadcastChannel>>(null);

  // Hidratar después del mount
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Coerción one-shot del modo `system` legacy (DS-5): con `enableSystem={false}`
  // next-themes ya no resuelve `system`, pero un valor viejo persistido en
  // localStorage['sc-theme'] podría sobrevivir. Al hidratar, si el tema es
  // 'system' lo resolvemos a 'light' (default binario de la familia) — así
  // ninguna preferencia `system` sobrevive tampoco del lado cliente.
  useEffect(() => {
    if (!isHydrated) return;
    if (theme === 'system') {
      setNextTheme('light');
    }
  }, [isHydrated, theme, setNextTheme]);

  // Inicializar BroadcastChannel para sincronización entre pestañas
  useEffect(() => {
    if (!isHydrated) return;

    const channel = createThemeBroadcastChannel((newTheme) => {
      // Recibimos cambio de otra pestaña. NO comparamos contra `theme`: este
      // callback vive en el closure del efecto (deps [isHydrated]) y capturaría
      // un `theme` STALE → pestañas desincronizadas. `setNextTheme` al mismo
      // valor es idempotente (next-themes no re-renderiza ni re-emite: el efecto
      // de broadcast depende de `theme` y no re-dispara si el valor no cambió),
      // así que aplicar siempre es seguro y sin loop.
      setNextTheme(newTheme);
    });

    broadcastRef.current = channel;

    return () => {
      channel?.close();
    };
  }, [isHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Broadcast cuando cambia el tema localmente
  useEffect(() => {
    if (!isHydrated || !theme) return;

    // Notificar a otras pestañas
    broadcastRef.current?.broadcast(theme as ThemeMode);
  }, [theme, isHydrated]);

  // Cargar preferencia del usuario desde el servidor
  useEffect(() => {
    if (!userId || !isHydrated) return;

    const loadUserPreference = async () => {
      try {
        const response = await fetch('/web/endpoints/user/preferences/theme');
        if (response.ok) {
          const data = await response.json();
          if (data.tema && data.tema !== theme) {
            setNextTheme(data.tema);
          }
        }
      } catch {
        // Silencioso - usa el tema local
      }
    };

    loadUserPreference();
  }, [userId, isHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Función para cambiar tema
  const setTheme = useCallback((newTheme: ThemeMode) => {
    setNextTheme(newTheme);
  }, [setNextTheme]);

  // Toggle entre light y dark
  const toggleTheme = useCallback(() => {
    const current = resolvedTheme as ResolvedTheme;
    setNextTheme(current === 'light' ? 'dark' : 'light');
  }, [resolvedTheme, setNextTheme]);

  // Función para actualizar userId (usado desde componentes que tienen acceso a UserContext)
  const setUserId = useCallback((newUserId: string | number | null) => {
    setUserIdState(newUserId);
  }, []);

  // Sincronizar con servidor (para usuarios autenticados)
  const syncToServer = useCallback(async () => {
    if (!userId || isSyncing) return;

    setIsSyncing(true);
    try {
      await fetch('/web/endpoints/user/preferences/theme', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tema: theme }),
      });
    } catch {
      // Silencioso - la preferencia se mantiene local
    } finally {
      setIsSyncing(false);
    }
  }, [userId, theme, isSyncing]);

  // Auto-sync cuando cambia el tema (debounced)
  useEffect(() => {
    if (!userId || !isHydrated) return;

    const timeoutId = setTimeout(() => {
      syncToServer();
    }, syncDebounceMs);

    return () => clearTimeout(timeoutId);
  }, [theme, userId, isHydrated, syncDebounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<ThemeContextValue>(() => ({
    theme: (theme === 'light' || theme === 'dark' ? theme : 'light') as ThemeMode,
    resolvedTheme: resolvedTheme as ResolvedTheme | undefined,
    themeName: 'default' as ThemeName,
    isLoading: !isHydrated,
    isHydrated,
    setTheme,
    toggleTheme,
    syncToServer,
    setUserId,
  }), [theme, resolvedTheme, isHydrated, setTheme, toggleTheme, syncToServer, setUserId]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// =================================
// PUBLIC PROVIDER
// =================================

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  userId,
}: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={defaultTheme}
      enableSystem={false}
      disableTransitionOnChange={false}
      storageKey={THEME_CONFIG.storageKey}
    >
      <InternalThemeProvider initialUserId={userId}>
        {children}
      </InternalThemeProvider>
    </NextThemesProvider>
  );
}

// =================================
// HOOKS
// =================================

/**
 * Hook para acceder al contexto de tema
 *
 * @example
 * ```tsx
 * const { theme, setTheme, toggleTheme } = useTheme();
 *
 * return (
 *   <button onClick={toggleTheme}>
 *     {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
 *   </button>
 * );
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error(
      'useTheme debe usarse dentro de ThemeProvider. ' +
      'Asegúrate de que ThemeProvider envuelva tu aplicación en providers.jsx'
    );
  }

  return context;
}

/**
 * Hook simplificado solo para el tema resuelto
 * Útil cuando solo necesitas saber si es light o dark
 */
export function useResolvedTheme(): ResolvedTheme | undefined {
  const { resolvedTheme } = useTheme();
  return resolvedTheme;
}

/**
 * Hook para verificar si el tema está cargado
 * Útil para evitar flash de contenido incorrecto
 */
export function useThemeLoaded(): boolean {
  const { isHydrated } = useTheme();
  return isHydrated;
}

/**
 * Hook para sincronizar userId con el ThemeContext
 * Útil para componentes que tienen acceso a UserContext
 *
 * @example
 * ```tsx
 * function UserThemeSync() {
 *   const { user } = useUser();
 *   useThemeUserSync(user?.id);
 *   return null;
 * }
 * ```
 */
export function useThemeUserSync(userId: string | number | null | undefined): void {
  const { setUserId } = useTheme();

  useEffect(() => {
    if (userId !== undefined) {
      setUserId(userId ?? null);
    }
  }, [userId, setUserId]);
}

// Export constants for external use
export { THEME_CONFIG };
